import React, { useState, useRef } from 'react';
import {
  FaReceipt, FaPlus, FaTimes, FaCheck, FaDollarSign, FaCalendarAlt,
  FaMapMarkerAlt, FaTrash, FaLock, FaCamera, FaSpinner, FaUpload
} from 'react-icons/fa';
import { analyzeReceipt } from '../../api/client';

const CURRENCY_OPTIONS = [
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '\u20AC EUR', symbol: '\u20AC' },
  { value: 'GBP', label: '\u00A3 GBP', symbol: '\u00A3' },
  { value: 'JPY', label: '\u00A5 JPY', symbol: '\u00A5' },
  { value: 'AUD', label: 'A$ AUD', symbol: 'A$' },
  { value: 'IDR', label: 'Rp IDR', symbol: 'Rp' },
  { value: 'CHF', label: 'CHF', symbol: 'CHF' },
  { value: 'INR', label: '\u20B9 INR', symbol: '\u20B9' },
];

const DEMO_EXPENSE_LIMIT = 2;
const CATEGORIES = ['Food', 'Transport', 'Accommodation', 'Activities', 'Shopping', 'Other'];

const DemoExpenses = ({ destination, onGetStarted }) => {
  const [expenses, setExpenses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [formData, setFormData] = useState({
    expense_date: new Date().toISOString().slice(0, 16),
    place_name: '',
    amount: '',
    description: '',
    category: 'Food',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const fileInputRef = useRef(null);

  const currencySymbol = CURRENCY_OPTIONS.find(c => c.value === currency)?.symbol || '$';
  const totalAmount = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const isLimitReached = expenses.length >= DEMO_EXPENSE_LIMIT;

  const processReceiptFile = async (file) => {
    if (!file) return;

    setReceiptFile(file);
    setAnalyzeError(null);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setReceiptPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(null);
    }

    // Analyze receipt with AI
    setIsAnalyzing(true);
    try {
      const extractedData = await analyzeReceipt(file);
      // Auto-fill form with extracted data
      setFormData(prev => ({
        ...prev,
        place_name: extractedData.place_name || prev.place_name,
        amount: extractedData.amount ? extractedData.amount.toString() : prev.amount,
        currency: extractedData.currency || prev.currency,
        description: extractedData.description || prev.description,
        expense_date: extractedData.expense_date
          ? new Date(extractedData.expense_date).toISOString().slice(0, 16)
          : prev.expense_date,
      }));
      if (extractedData.currency) {
        setCurrency(extractedData.currency);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAnalyzeError('Sign up for a free account to use receipt scanning. You can still enter details manually.');
      } else {
        // Silent fail - user can fill manually
        console.log('Receipt analysis not available in demo');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) processReceiptFile(file);
    e.target.value = '';
  };

  const handleAddExpense = () => {
    if (!formData.amount || !formData.description) return;
    if (isLimitReached) return;

    const newExpense = {
      id: `demo-${Date.now()}`,
      ...formData,
      amount: parseFloat(formData.amount),
      created_at: new Date().toISOString(),
      hasReceipt: !!receiptFile,
    };

    setExpenses(prev => [...prev, newExpense]);
    setFormData({
      expense_date: new Date().toISOString().slice(0, 16),
      place_name: '',
      amount: '',
      description: '',
      category: 'Food',
    });
    setReceiptFile(null);
    setReceiptPreview(null);
    setAnalyzeError(null);
    setShowAddForm(false);
  };

  const handleDeleteExpense = (id) => {
    setExpenses(prev => prev.filter(e => e.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Demo Banner */}
      <div className="flex items-center justify-between p-3 rounded-lg border bg-amber-50 border-amber-200">
        <p className="text-sm font-medium text-amber-700">
          Demo mode: {expenses.length}/{DEMO_EXPENSE_LIMIT} expenses (local only). Subscribe to Solo plan for unlimited expense tracking.
        </p>
      </div>

      {/* Currency Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-neutral-800 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
            <FaReceipt className="text-white text-xl" />
          </div>
          Expenses
        </h3>
        <select
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:border-amber-500"
        >
          {CURRENCY_OPTIONS.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Total */}
      {expenses.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
          <div className="flex items-center justify-between">
            <span className="text-amber-800 font-medium">Total Expenses</span>
            <span className="text-2xl font-bold text-amber-900">{currencySymbol}{totalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Expense List */}
      {expenses.length > 0 && (
        <div className="space-y-3">
          {expenses.map((expense) => (
            <div key={expense.id} className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <FaDollarSign className="text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">{expense.description}</p>
                  <p className="text-sm text-neutral-500 flex items-center gap-2">
                    <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">{expense.category}</span>
                    {expense.place_name && <><FaMapMarkerAlt className="text-xs" /> {expense.place_name}</>}
                    {expense.hasReceipt && <><FaReceipt className="text-xs text-green-500" /> Receipt</>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-neutral-800">{currencySymbol}{expense.amount.toFixed(2)}</span>
                <button onClick={() => handleDeleteExpense(expense.id)} className="p-2 text-neutral-400 hover:text-red-500 transition-colors">
                  <FaTrash className="text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Button or Form */}
      {!showAddForm ? (
        <button
          onClick={() => !isLimitReached && setShowAddForm(true)}
          disabled={isLimitReached}
          className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
            isLimitReached
              ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-amber-400 to-amber-500 text-white hover:shadow-lg hover:scale-[1.02]'
          }`}
        >
          {isLimitReached ? (
            <><FaLock /> Demo limit reached - Subscribe to Solo plan</>
          ) : (
            <><FaPlus /> Add Expense</>
          )}
        </button>
      ) : (
        <div className="bg-white rounded-xl shadow-md border border-neutral-200 p-6 space-y-4 animate-slide-down">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-neutral-800">Add Expense</h4>
            <button onClick={() => { setShowAddForm(false); setReceiptFile(null); setReceiptPreview(null); setAnalyzeError(null); }} className="text-neutral-400 hover:text-neutral-600">
              <FaTimes />
            </button>
          </div>

          {/* Receipt Scanner */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
            <label className="flex items-center gap-2 text-amber-800 font-semibold mb-3 text-sm">
              <FaReceipt /> Scan Receipt (auto-fills details)
            </label>
            <div className="flex items-center gap-3">
              <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleFileSelect} className="hidden" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isAnalyzing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-amber-300 rounded-lg text-amber-700 hover:bg-amber-50 font-medium text-sm disabled:opacity-50"
              >
                {isAnalyzing ? <><FaSpinner className="animate-spin" /> Analyzing...</> : <><FaCamera /> Upload Receipt</>}
              </button>
              {receiptFile && (
                <span className="text-sm text-neutral-600 flex items-center gap-1">
                  <FaCheck className="text-green-500" /> {receiptFile.name}
                </span>
              )}
            </div>
            {receiptPreview && (
              <div className="mt-3 relative inline-block">
                <img src={receiptPreview} alt="Receipt" className="max-h-32 rounded-lg border border-neutral-200" />
                <button onClick={() => { setReceiptFile(null); setReceiptPreview(null); }} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600">
                  <FaTimes className="text-xs" />
                </button>
              </div>
            )}
            {analyzeError && (
              <div className="mt-2 text-sm text-amber-700 bg-amber-100 rounded-lg p-2">
                {analyzeError}
                {onGetStarted && (
                  <button onClick={onGetStarted} className="ml-2 font-bold text-amber-900 underline hover:no-underline">Sign Up Free</button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Description *</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Lunch at local restaurant"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">{currencySymbol}</span>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full pl-8 pr-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-amber-500"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Place</label>
              <input
                type="text"
                value={formData.place_name}
                onChange={(e) => setFormData(prev => ({ ...prev, place_name: e.target.value }))}
                placeholder="e.g., Shibuya, Tokyo"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Date & Time</label>
              <input
                type="datetime-local"
                value={formData.expense_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expense_date: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          <button
            onClick={handleAddExpense}
            disabled={!formData.amount || !formData.description}
            className="w-full py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <FaCheck /> Add Expense
          </button>
        </div>
      )}

      {/* Empty State */}
      {expenses.length === 0 && !showAddForm && (
        <div className="text-center py-8 bg-neutral-50 rounded-xl border-2 border-dashed border-neutral-300">
          <div className="w-16 h-16 bg-neutral-200 rounded-full flex items-center justify-center mx-auto mb-3">
            <FaReceipt className="text-3xl text-neutral-400" />
          </div>
          <p className="text-neutral-600 font-medium">No expenses yet. Track your trip spending!</p>
        </div>
      )}
    </div>
  );
};

export default DemoExpenses;
